export type UboKycLiveness = {
  ok?: boolean;
  pass?: boolean;
  label?: string | null;
  mrz_label?: string | null;
  selfie_label?: string | null;
  spoof_score?: number | null;
  warnings?: number | null;
  // Allow backend to add extra keys without TS explosions
  [key: string]: any;
};

export type Ubo = {
  id: number;
  business_kyb_data_id: number;

  // Identity fields
  first_name: string;
  last_name: string;
  birthdate: string;
  nationality: string;
  residential_address: string;
  ownership_percent: number;
  is_pep?: boolean | number | null;
  pep_details?: string | null;
  source_of_income?: string | null;
  source_of_income_other?: string | null;

  // UBO status (business logic)
  status: "pending" | "approved" | "rejected" | string;

  // Admin-only flags (still allowed on the type; no user upload flow anymore)
  require_id_reupload?: boolean | number;
  require_poa_reupload?: boolean | number;
  review_notes?: string | null;

  /** Optional soft-deletion flag */
  pending_deletion?: boolean | number;

  /** Optional per-UBO KYC link from backend */
  kyc_link?: string | null;

  /** Optional liveness snapshot from KycKybService::getUboLivenessSnapshot() */
  kyc_liveness?: UboKycLiveness | null;
};

/** Internal: unwrap {data: ...} or return body as-is */
async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = (j?.message || j?.error || msg) as string;
    } catch {}
    throw new Error(msg);
  }
  try {
    const j = await res.json();
    if (j && typeof j === "object" && "data" in j) {
      return (j as any).data as T;
    }
    return j as T;
  } catch {
    // no body
    return undefined as unknown as T;
  }
}

/** List UBOs -> Ubo[] */
export async function apiUboList(): Promise<Ubo[]> {
  const res = await fetch("/xapi/ubos", { credentials: "include" });
  return unwrap<Ubo[]>(res);
}

/** Show UBO -> Ubo */
export async function apiUboShow(id: number): Promise<Ubo> {
  const res = await fetch(`/xapi/ubos/${id}`, { credentials: "include" });
  return unwrap<Ubo>(res);
}

/** Create UBO -> Ubo
 *
 * NOTE: identity-only now. FormData should contain only the identity fields:
 * - first_name, last_name, birthdate, nationality, residential_address, ownership_percent, etc.
 */
export async function apiUboCreate(form: FormData): Promise<Ubo> {
  const res = await fetch("/xapi/ubos", {
    method: "POST",
    body: form,
    credentials: "include",
  });
  return unwrap<Ubo>(res);
}

/** Update UBO -> Ubo
 *
 * Same as create: identity-only fields.
 */
export async function apiUboUpdate(id: number, form: FormData): Promise<Ubo> {
  const res = await fetch(`/xapi/ubos/${id}`, {
    method: "POST", // Many Laravel routes use POST with _method=PUT; FormData can include it
    body: (() => {
      if (!form.has("_method")) form.append("_method", "PUT");
      return form;
    })(),
    credentials: "include",
  });
  return unwrap<Ubo>(res);
}

/** Delete UBO -> { ok: true } */
export async function apiUboDelete(id: number): Promise<{ ok: boolean }> {
  const res = await fetch(`/xapi/ubos/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  // Accept either {ok:true} or empty 204
  try {
    const j = await res.json();
    return (j as any) ?? { ok: true };
  } catch {
    return { ok: true };
  }
}
